import { useMemo, useSyncExternalStore } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { uploadWithProgress } from "@/lib/upload-with-progress";
import { mergeUploadedWeighingPhoto } from "@/api/hooks/use-weighing-photos";
import type { PhotoCategoryId, PlatformWeighingId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type PhotoResponse = components["schemas"]["PhotoResponse"];

export type UploadStatus = "queued" | "uploading" | "done" | "error";

/** What a queued file belongs to. The tray, the concurrency cap and the retry
 *  path are owner-agnostic. Build photos and cal/toe/yaw photos are
 *  deliberately absent -- they upload through use-photos.ts direct mutations.
 *  (Service-record photos moved to servicing.sailgp.tech with Maintenance.) */
export type UploadOwner = {
  kind: "weighing";
  weighingId: PlatformWeighingId;
  categoryId: PhotoCategoryId;
  allowMultiple: boolean;
};

export interface UploadJob {
  id: string;
  owner: UploadOwner;
  /** Human label for the tray row (a photo category). */
  label: string;
  /** Kept so a failed job can be retried. */
  file: File;
  previewUrl: string;
  progress: number;
  status: UploadStatus;
  error?: string;
  /** Resolves the job's `done` promise; resolving twice is a harmless no-op
   *  (a tray retry after a settled failure just re-runs the upload). */
  settle?: (ok: boolean) => void;
}

export interface EnqueuedUpload {
  id: string;
  /** Resolves true when the upload lands, false on the first failure — never
   *  rejects. Lets a caller await queue jobs (e.g. captures before sign-off)
   *  while keeping tray visibility, progress, and the concurrency cap. */
  done: Promise<boolean>;
}

// Browsers cap ~6 connections/host and mobile uplinks choke well before that;
// 3 keeps each upload's progress meaningful while still overlapping them.
const MAX_CONCURRENT = 3;
const DONE_TTL_MS = 4000;

let jobs: UploadJob[] = [];
const listeners = new Set<() => void>();
let queryClient: QueryClient | null = null;
let seq = 0;

function notify() {
  listeners.forEach((l) => l());
}

function patch(id: string, changes: Partial<UploadJob>) {
  jobs = jobs.map((j) => (j.id === id ? { ...j, ...changes } : j));
  notify();
}

/** Wired once at app start so background jobs can update the photo cache even
 *  after the drawer that started them has unmounted. */
export function setUploadQueryClient(client: QueryClient) {
  queryClient = client;
}

function pump() {
  // run() flips the job to "uploading" synchronously, so each find() picks the
  // next still-queued job — fill free slots up to the concurrency cap.
  let active = jobs.filter((j) => j.status === "uploading").length;
  while (active < MAX_CONCURRENT) {
    const next = jobs.find((j) => j.status === "queued");
    if (!next) break;
    run(next.id);
    active++;
  }
}

function run(id: string) {
  const job = jobs.find((j) => j.id === id);
  if (!job) return;
  patch(id, { status: "uploading", progress: 0, error: undefined });

  const formData = new FormData();
  formData.append("file", job.file);
  const owner = job.owner;
  formData.append("category_id", owner.categoryId);

  // ONE file per request (constraint photo-upload-single-file); a
  // multi-select fans out into one queued job each and this cap paces them.
  uploadWithProgress<PhotoResponse>({
    url: `/api/platform-weighings/${owner.weighingId}/photos`,
    formData,
    onProgress: (progress) => patch(id, { progress }),
  })
    .then(({ data }) => {
      if (queryClient) {
        mergeUploadedWeighingPhoto(queryClient, owner.weighingId, data, owner.allowMultiple);
      }
      patch(id, { status: "done", progress: 100 });
      job.settle?.(true);
      setTimeout(() => dismissUpload(id), DONE_TTL_MS);
    })
    .catch((err: Error) => {
      patch(id, { status: "error", error: err.message });
      job.settle?.(false);
    })
    .finally(pump);
}

function enqueue(owner: UploadOwner, label: string, file: File): EnqueuedUpload {
  const id = `upload-${++seq}`;
  let settle!: (ok: boolean) => void;
  const done = new Promise<boolean>((resolve) => {
    settle = resolve;
  });
  jobs = [
    ...jobs,
    {
      id,
      owner,
      label,
      file,
      previewUrl: URL.createObjectURL(file),
      progress: 0,
      status: "queued",
      settle,
    },
  ];
  notify();
  pump();
  return { id, done };
}

export function enqueueUpload(args: {
  weighingId: PlatformWeighingId;
  categoryId: PhotoCategoryId;
  label: string;
  file: File;
  allowMultiple?: boolean;
}): EnqueuedUpload {
  return enqueue(
    {
      kind: "weighing",
      weighingId: args.weighingId,
      categoryId: args.categoryId,
      allowMultiple: args.allowMultiple ?? false,
    },
    args.label,
    args.file,
  );
}

export function retryUpload(id: string) {
  patch(id, { status: "queued", progress: 0, error: undefined });
  pump();
}

export function dismissUpload(id: string) {
  const job = jobs.find((j) => j.id === id);
  if (job) URL.revokeObjectURL(job.previewUrl);
  jobs = jobs.filter((j) => j.id !== id);
  notify();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return jobs;
}

export function useUploadJobs(): UploadJob[] {
  return useSyncExternalStore(subscribe, getSnapshot);
}

/** A stable key of every in-flight (weighingId:categoryId) pair. Returns the
 *  same string on progress ticks that don't change *which* uploads are live, so
 *  useSyncExternalStore (Object.is on the string) skips the re-render — the grid
 *  only cares about the set of categories, not the percent. */
function getActiveCategoryKey() {
  const parts: string[] = [];
  for (const j of jobs) {
    if (j.status === "queued" || j.status === "uploading") {
      parts.push(`${j.owner.weighingId}:${j.owner.categoryId}`);
    }
  }
  return parts.sort().join("|");
}

/** Categories of a weighing with an upload still queued or in flight — drives
 *  the per-slot spinner in the photo grid. Subscribes to the active-set key,
 *  not the jobs array, so percent ticks don't re-render the whole section. */
export function useUploadingCategoryIds(weighingId: PlatformWeighingId): Set<PhotoCategoryId> {
  const key = useSyncExternalStore(subscribe, getActiveCategoryKey);
  return useMemo(() => {
    const ids = new Set<PhotoCategoryId>();
    if (!key) return ids;
    const prefix = `${weighingId}:`;
    for (const part of key.split("|")) {
      if (part.startsWith(prefix)) ids.add(part.slice(prefix.length) as PhotoCategoryId);
    }
    return ids;
  }, [key, weighingId]);
}
